import { useMemo, useRef, useState } from 'react'
import { motion } from 'motion/react'
import {
  Download,
  FolderOpen,
  HardDrive,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { BrandMark } from './BrandMark'
import { locale, t, type Lang } from '../i18n'
import type { ProjectMeta } from '../types'

interface Props {
  projects: ProjectMeta[]
  activeId: string | null
  demoActive: boolean
  lang: Lang
  onLang: (lang: Lang) => void
  onOpen: (id: string) => void
  onNew: () => void
  onNewFolder: () => void
  onDemo: () => void
  onRename: (meta: ProjectMeta) => void
  onDelete: (meta: ProjectMeta) => void
  onDownload: (meta: ProjectMeta) => void
  onLegal: (key: 'privacy' | 'terms' | 'security') => void
  onCloseMobile: () => void
}

function relativeTime(timestamp: number): string {
  const delta = Date.now() - timestamp
  const minutes = Math.floor(delta / 60000)
  if (minutes < 1) return t('project.justNow')
  if (minutes < 60) return t('project.minutesAgo', { n: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('project.hoursAgo', { n: hours })
  if (hours < 48) return t('project.yesterday')
  const days = Math.floor(hours / 24)
  return t('project.daysAgo', { n: days })
}

function money(value: number): string {
  return `$${value.toLocaleString(locale(), { maximumFractionDigits: value >= 1000 ? 0 : 2 })}`
}

export function ProjectRail({
  projects,
  activeId,
  demoActive,
  lang,
  onLang,
  onOpen,
  onNew,
  onNewFolder,
  onDemo,
  onRename,
  onDelete,
  onDownload,
  onLegal,
  onCloseMobile,
}: Props) {
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return projects
    return projects.filter((project) => project.name.toLowerCase().includes(needle))
  }, [projects, query])

  return (
    <aside className="rail" aria-label={t('rail.projects')}>
      <div className="rail-head">
        <div className="brand">
          <span className="brand-mark">
            <BrandMark size={21} />
          </span>
          <span className="brand-type">
            <strong>PCBA</strong>
            <span>GLOBAL VIEW</span>
          </span>
        </div>
        <button className="icon-btn rail-close" type="button" onClick={onCloseMobile} aria-label={t('common.close')}>
          <X size={15} />
        </button>
      </div>

      <div className="new-board-row">
        <button className="new-board" type="button" onClick={onNew}>
          <Plus size={15} />
          <span>{t('rail.new')}</span>
          <kbd>U</kbd>
        </button>
        <button
          className="new-board-folder"
          type="button"
          onClick={onNewFolder}
          aria-label={t('rail.newFolder')}
          title={t('rail.newFolder')}
        >
          <FolderOpen size={15} />
        </button>
      </div>

      <div className="rail-search">
        <Search size={13} />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('rail.search')}
          aria-label={t('rail.search')}
        />
      </div>

      <div className="rail-scroll">
        <button className={`demo-card ${demoActive ? 'is-active' : ''}`} type="button" onClick={onDemo}>
          <span className="demo-thumb" aria-hidden="true">
            <BrandMark size={20} />
          </span>
          <span className="demo-copy">
            <strong>{t('rail.demo')}</strong>
            <small>{t('rail.demoMeta')}</small>
          </span>
          <span className="demo-tag">DEMO</span>
        </button>

        {projects.length > 0 ? (
          <div className="rail-section">
            <span>{t('rail.projects')}</span>
            <span>{projects.length}</span>
          </div>
        ) : null}

        {filtered.length === 0 ? (
          demoActive && !query ? null : (
            <div className="rail-empty">
              <strong>{query ? t('palette.noResults') : t('rail.emptyTitle')}</strong>
              {query ? null : <p>{t('rail.emptyBody')}</p>}
            </div>
          )
        ) : (
          <div className="project-list">
            {filtered.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                active={project.id === activeId}
                onOpen={() => onOpen(project.id)}
                onRename={() => onRename(project)}
                onDelete={() => onDelete(project)}
                onDownload={() => onDownload(project)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="rail-foot">
        <div className="local-badge">
          <HardDrive size={12} />
          <span>{t('rail.local')}</span>
        </div>
        <div className="rail-foot-row">
          <div className="lang-switch" role="group" aria-label="Language">
            <button
              type="button"
              className={lang === 'zh' ? 'is-active' : ''}
              onClick={() => onLang('zh')}
              aria-pressed={lang === 'zh'}
            >
              中
            </button>
            <button
              type="button"
              className={lang === 'en' ? 'is-active' : ''}
              onClick={() => onLang('en')}
              aria-pressed={lang === 'en'}
            >
              EN
            </button>
          </div>
          <div className="legal-links">
            <button type="button" onClick={() => onLegal('privacy')}>{t('rail.privacy')}</button>
            <button type="button" onClick={() => onLegal('terms')}>{t('rail.terms')}</button>
            <button type="button" onClick={() => onLegal('security')}>{t('rail.filesShort')}</button>
          </div>
        </div>
      </div>
    </aside>
  )
}

function ProjectCard({
  project,
  active,
  onOpen,
  onRename,
  onDelete,
  onDownload,
}: {
  project: ProjectMeta
  active: boolean
  onOpen: () => void
  onRename: () => void
  onDelete: () => void
  onDownload: () => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const close = (): void => setMenuOpen(false)

  return (
    <div className={`project-card ${active ? 'is-active' : ''}`}>
      {active ? <motion.span className="active-bar" layoutId="rail-active" transition={{ type: 'spring', stiffness: 420, damping: 34 }} /> : null}
      <button className="project-open" type="button" onClick={onOpen}>
        <span className="project-thumb">
          {project.thumbnail ? <img src={project.thumbnail} alt="" loading="lazy" /> : <BrandMark size={18} />}
        </span>
        <span className="project-copy">
          <strong title={project.name}>{project.name}</strong>
          <small>
            {relativeTime(project.updatedAt)}
            {project.total > 0 ? ` · ${money(project.total)}` : ''}
          </small>
        </span>
      </button>
      <div className="project-more" ref={menuRef}>
        <button
          className="icon-btn tiny"
          type="button"
          aria-label={t('project.more')}
          aria-expanded={menuOpen}
          onClick={(event) => {
            event.stopPropagation()
            setMenuOpen((value) => !value)
          }}
        >
          <MoreHorizontal size={14} />
        </button>
        {menuOpen ? (
          <>
            <button className="menu-scrim" type="button" aria-label={t('common.close')} onClick={close} />
            <div className="project-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => { close(); onRename() }}>
                <Pencil size={13} /> {t('project.rename')}
              </button>
              <button type="button" role="menuitem" onClick={() => { close(); onDownload() }}>
                <Download size={13} /> {t('project.download')}
              </button>
              <button className="is-danger" type="button" role="menuitem" onClick={() => { close(); onDelete() }}>
                <Trash2 size={13} /> {t('project.delete')}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}
